import GeoJSON from "ol/format/GeoJSON";
import Style from "ol/style/Style";
import { Stroke } from "ol/style";
import { toLonLat } from "ol/proj";
import { project_point_on_line } from "../util";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { Infra3DDataProvider } from "../../../api/Infra3DDataProvider_v2";
export class RoutesLayer {
    constructor(navigator, map, overview_configs, olstyle, olstyle_area, tooltip_callback, segmentedonly = false) {
        this._layers = [];
        this._map = null;
        this._navigator = null;
        this._selected_feature = null;
        this._selectionLayers = [];
        this._map = map;
        this._navigator = navigator;
        this.init(overview_configs, olstyle, olstyle_area, segmentedonly);
        this._add_interactions();
    }
    get layers() {
        return this._layers;
    }
    init(overview_configs, olstyle, olstyle_area, segmentedonly = false) {
        overview_configs = overview_configs || [
            {
                type: "routes",
                zoomrange: [14, 19],
            },
            {
                type: "routes_simplified",
                zoomrange: [12, 14],
            },
            {
                type: "routes_outline",
                zoomrange: [0, 12],
            },
        ];
        for (const overview_config of overview_configs) {
            let loaderf;
            let style = olstyle;
            if (overview_config.type == "routes_outline")
                style = olstyle_area;
            const zoomrange_defaults = [0, 19];
            const zoomrange = overview_config.zoomrange || zoomrange_defaults;
            const layer = new VectorLayer({
                map: this._map,
                source: new VectorSource({
                    format: new GeoJSON({
                        featureProjection: "EPSG:3857",
                    }),
                    loader: (extent, resolution, projection, success, failure) => {
                        const onerror = () => {
                            layer.getSource().removeLoadedExtent(extent);
                            failure();
                        };
                        if (!(this._navigator.api.data instanceof Infra3DDataProvider)) {
                            return;
                        }
                        switch (overview_config.type) {
                            case "routes":
                                loaderf = this._navigator.api.data.getRoutes({
                                    simplify: 1,
                                    segmentedonly: segmentedonly,
                                });
                                break;
                            case "routes_simplified":
                                loaderf = this._navigator.api.data.getRoutes({
                                    simplify: 10,
                                    segmentedonly: segmentedonly,
                                });
                                break;
                            case "routes_outline":
                                loaderf = this._navigator.api.data.getRoutesOutline();
                                break;
                        }
                        loaderf
                            .then((geojson) => {
                            const features = layer.getSource().getFormat().readFeatures(geojson);
                            layer.getSource().addFeatures(features);
                            success(features);
                        })
                            .catch(onerror);
                    },
                }),
                zIndex: 200,
                maxZoom: zoomrange[1],
                minZoom: zoomrange[0],
                style: style,
            });
            this._layers.push(layer);
            if (overview_config.type == "routes" || overview_config.type == "routes_simplified") {
                const select_style = new Style({
                    stroke: new Stroke({
                        color: "rgba(204, 102, 51,1.0)",
                        width: 5.0,
                    }),
                    zIndex: 210,
                });
                const selection_layer = new VectorLayer({
                    map: this._map,
                    source: layer.getSource(),
                    style: (feature) => {
                        if (!feature || !this._selected_feature)
                            return new Style();
                        // if (!feature.getProperties() || !this._selected_feature.getProperties()) return new Style();
                        if (feature == this._selected_feature) {
                            return select_style;
                        }
                        return new Style();
                    },
                    zIndex: 210,
                });
                this._selectionLayers.push(selection_layer);
            }
        }
    }
    _add_interactions() {
        this._map.on("pointermove", (evt) => {
            if (evt.dragging)
                return;
            this._layers.forEach((layer) => {
                const current_zoom = this._map.getView().getZoom();
                if (current_zoom <= layer.getMinZoom() || current_zoom > layer.getMaxZoom())
                    return;
                layer.getFeatures(evt.pixel).then((features) => {
                    if (!features.length) {
                        this._selected_feature = null;
                        this._selectionLayers.forEach((selection_layer) => {
                            selection_layer.changed();
                        });
                        // Tooltip currently deactivated, as not active in routes
                        // if (tooltip_callback) tooltip_callback(evt.coordinate, null);
                        return;
                    }
                    const feature = features[0];
                    if (!feature) {
                        return;
                    }
                    if (feature.getGeometry().getType() !== "LineString") {
                        return;
                    }
                    this._selected_feature = feature;
                    this._selectionLayers.forEach((selection_layer) => {
                        selection_layer.changed();
                    });
                });
            });
        });
        this._map.on("click", (evt) => {
            if (!this._selected_feature) {
                // we also need to look for features on a click for tablet compatibility
                const features = this._map.getFeaturesAtPixel(evt.pixel, {
                    hitTolerance: 10, // in css pixel
                    layerFilter: (layer) => {
                        if (!(layer instanceof VectorLayer))
                            return false;
                        return this._layers.includes(layer);
                    },
                });
                if (features.length === 0) {
                    return;
                }
                this._selected_feature = features[0]; // always pick the first feature -> hypothesis: getFeaturesAtPixel seems to return the closest feature first
                this._selectionLayers.forEach((selection_layer) => {
                    selection_layer.changed();
                });
            }
            const coordinate = project_point_on_line(evt.coordinate, this._selected_feature);
            const lonlat = toLonLat(coordinate);
            const latlon = {
                lat: lonlat[1],
                lon: lonlat[0],
            };
            this._map.getTargetElement().style.cursor = "wait";
            this._navigator.movePosition$(latlon).subscribe((node) => {
                this._map.getTargetElement().style.cursor = "auto";
                // Reset selection after change
                this._selected_feature = null;
                this._selectionLayers.forEach((selection_layer) => {
                    selection_layer.changed();
                });
            }, (error) => {
                this._map.getTargetElement().style.cursor = "not-allowed";
                setTimeout(() => {
                    this._map.getTargetElement().style.cursor = "auto";
                }, 500);
                // Reset selection after change
                this._selected_feature = null;
                this._selectionLayers.forEach((selection_layer) => {
                    selection_layer.changed();
                });
            });
        });
    }
    setVisible(visible) {
        this.layers.forEach((layer) => {
            layer.setVisible(visible);
            layer.changed();
        });
    }
}
//# sourceMappingURL=RoutesLayer.js.map